<!-- EventConfigForm.vue — ส่วน "ราคา" และ "การรับสมัคร" ของอีเวนต์ (v3 split)
  เจ้าของสั่ง 2026-08-12: **สองสวิตช์ต้องแยกกัน** — ตั้งราคาไว้เก็บเงินเองโดยไม่เปิดรับสมัครก็ได้
  และเปิดรับสมัครโดยยังไม่กำหนดราคา (งานฟรี) ก็ได้
  ใช้ร่วมกันใน dialog สร้างอีเวนต์ + dialog แก้ไข (โครงเดียวไม่ drift)

  bound object = EventManager.cfgForm (mutate ตรง — พาเรนต์เป็นเจ้าของ blank/load/payload):
  { pricing: {enabled, price_mode, prices:[{level_value,age_min,age_max,gym_fee,organizer_fee}],
              fallback_gym_fee, fallback_organizer_fee},
    registration: {enabled, reg_open, reg_close, self_register, program_restricted} }
  ราคาสองขา (§7.7): gym_fee = รายได้ยิม (ฐาน VAT) · organizer_fee = เก็บแทนผู้จัดภายนอก (เจ้าหนี้อื่น)
  ยอดที่ผู้ปกครองเห็น/จ่าย = ผลรวมสองขาเสมอ -->
<template>
  <!-- eslint-disable vue/no-mutating-props — ดูเหตุผลใน script block -->
  <div class="evcfg">
    <!-- ══ สวิตช์ 1: ราคา ══ -->
    <div class="modal-sec mt">
      <span class="mdi mdi-cash-multiple"></span> {{ $t('evCfg.priceSection') }}
      <span class="idsw" :class="{ on: cfg.pricing.enabled }" style="margin-left:auto"
        @click="cfg.pricing.enabled = !cfg.pricing.enabled">
        <span class="track"></span><span class="thumb"></span>
      </span>
    </div>
    <div v-if="!cfg.pricing.enabled" class="evcfg-dim">{{ $t('evCfg.priceOffHint') }}</div>

    <template v-if="cfg.pricing.enabled">
      <div class="field full">
        <label>{{ $t('evCfg.priceMode') }}</label>
        <!-- radio group จริง (name เดียวกันทั้งชุด) — §7.6 ข้อ 6: mockup เคยติ๊กได้ 2 อันเพราะลืม name -->
        <div class="evcfg-radios">
          <label v-for="m in ['flat', 'age', 'level']" :key="m" class="evcfg-radio">
            <input type="radio" :name="'evPriceMode-' + uid" :value="m" v-model="cfg.pricing.price_mode" @change="onModeChange">
            {{ $t('evCfg.mode_' + m) }}
          </label>
        </div>
      </div>

      <div class="evcfg-tiers">
        <div class="evcfg-tier evcfg-tier-head">
          <span class="t-cond">{{ cfg.pricing.price_mode === 'age' ? $t('evCfg.ageRange')
            : cfg.pricing.price_mode === 'level' ? $t('evCfg.level') : $t('evCfg.flatLabel') }}</span>
          <span class="t-fee">{{ $t('evCfg.gymFee') }}</span>
          <span class="t-fee">{{ $t('evCfg.organizerFee') }}</span>
          <span class="t-sum">{{ $t('evCfg.total') }}</span>
          <span class="t-del"></span>
        </div>
        <div v-for="(p, i) in cfg.pricing.prices" :key="i" class="evcfg-tier">
          <span v-if="cfg.pricing.price_mode === 'age'" class="t-cond t-age">
            <input class="id-input" type="number" min="0" max="99" v-model="p.age_min" :placeholder="$t('evCfg.ageMin')">
            <span>–</span>
            <input class="id-input" type="number" min="0" max="99" v-model="p.age_max" :placeholder="$t('evCfg.ageMax')">
          </span>
          <span v-else-if="cfg.pricing.price_mode === 'level'" class="t-cond">
            <input class="id-input" type="number" min="0" max="999" step="0.01" v-model="p.level_value" :placeholder="$t('evCfg.levelPh')">
          </span>
          <span v-else class="t-cond evcfg-dim">{{ $t('evCfg.everyone') }}</span>
          <span class="t-fee"><id-money v-model="p.gym_fee"></id-money></span>
          <span class="t-fee"><id-money v-model="p.organizer_fee"></id-money></span>
          <span class="t-sum">{{ $money((Number(p.gym_fee) || 0) + (Number(p.organizer_fee) || 0)) }}</span>
          <span class="t-del">
            <span v-if="cfg.pricing.prices.length > 1" class="mdi mdi-delete-outline" style="color:var(--c-error);cursor:pointer" @click="cfg.pricing.prices.splice(i, 1)"></span>
          </span>
        </div>
        <button v-if="cfg.pricing.price_mode !== 'flat'" class="id-btn id-btn-ghost id-btn-sm" type="button" style="margin-top:6px" @click="addTier">
          <span class="mdi mdi-plus"></span> {{ $t('evCfg.addTier') }}
        </button>
        <div class="fhint" style="margin-top:6px">{{ $t('evCfg.twoLegHint') }}</div>
      </div>

      <div v-if="cfg.pricing.price_mode !== 'flat'" class="form-grid" style="margin-top:10px">
        <div class="field">
          <label>{{ $t('evCfg.fallbackGym') }}</label>
          <id-money v-model="cfg.pricing.fallback_gym_fee"></id-money>
        </div>
        <div class="field">
          <label>{{ $t('evCfg.fallbackOrganizer') }}</label>
          <id-money v-model="cfg.pricing.fallback_organizer_fee"></id-money>
        </div>
        <div class="field full"><div class="fhint">{{ $t('evCfg.fallbackHint') }}</div></div>
      </div>
    </template>

    <!-- ══ สวิตช์ 2: การรับสมัคร (อิสระจากราคา) ══ -->
    <div class="modal-sec mt">
      <span class="mdi mdi-calendar-check"></span> {{ $t('evCfg.regSection') }}
      <span class="idsw" :class="{ on: cfg.registration.enabled }" style="margin-left:auto"
        @click="cfg.registration.enabled = !cfg.registration.enabled">
        <span class="track"></span><span class="thumb"></span>
      </span>
    </div>
    <div v-if="!cfg.registration.enabled" class="evcfg-dim">{{ $t('evCfg.regOffHint') }}</div>

    <div v-if="cfg.registration.enabled" class="form-grid">
      <div class="field">
        <label>{{ $t('evCfg.regOpen') }}</label>
        <id-date v-model="cfg.registration.reg_open"></id-date>
      </div>
      <div class="field">
        <label>{{ $t('evCfg.regClose') }}</label>
        <id-date v-model="cfg.registration.reg_close"></id-date>
        <div class="fhint">{{ $t('evCfg.regCloseHint') }}</div>
      </div>
      <div class="field">
        <label class="evcfg-flag" @click="cfg.registration.program_restricted = !cfg.registration.program_restricted">
          <span class="id-check" :class="{ on: cfg.registration.program_restricted }"><span class="mdi mdi-check"></span></span>
          {{ $t('evCfg.programRestricted') }}
        </label>
      </div>
      <div class="field">
        <label class="evcfg-flag" @click="cfg.registration.self_register = !cfg.registration.self_register">
          <span class="id-check" :class="{ on: cfg.registration.self_register }"><span class="mdi mdi-check"></span></span>
          {{ $t('evCfg.selfRegister') }}
        </label>
        <div class="fhint">{{ $t('evCfg.selfRegisterHint') }}</div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable vue/no-mutating-props -- cfg เป็นฟอร์ม object ที่ EventManager สร้าง/ล้าง/อ่านเอง
   (blankCfg/loadCfg/cfgPayload) — component นี้เป็นแค่ "หน้ากาก" ของฟอร์มเดียวกันใน 2 dialog
   การ mutate ตรงคือพฤติกรรมที่ตั้งใจ ไม่ใช่ prop ข้อมูลไหลลง */
let uidSeq = 0
export default {
  name: 'EventConfigForm',
  props: { cfg: { type: Object, required: true } },
  data() { return { uid: ++uidSeq } },
  methods: {
    addTier() { this.cfg.pricing.prices.push({ level_value: '', age_min: '', age_max: '', gym_fee: '', organizer_fee: '' }) },
    onModeChange() {
      // flat = แถวเดียวเสมอ — ตัดให้เหลือแถวแรกตอนสลับกลับมา
      if (this.cfg.pricing.price_mode === 'flat' && this.cfg.pricing.prices.length > 1) {
        this.cfg.pricing.prices = [this.cfg.pricing.prices[0]]
      }
    },
  },
}
</script>

<style scoped>
.evcfg-dim { color: var(--c-text-muted); font-size: 12px; padding: 4px 0 8px; }
.evcfg .modal-sec { display: flex; align-items: center; gap: 8px; }
.evcfg-flag { display: inline-flex; align-items: center; gap: 8px; cursor: pointer; }
.evcfg-radios { display: flex; gap: 16px; flex-wrap: wrap; padding: 4px 0; }
.evcfg-radio { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; cursor: pointer; }
.evcfg-tiers { margin-top: 6px; }
.evcfg-tier { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.evcfg-tier-head { font-size: 12px; color: var(--c-text-muted); font-weight: 600; }
.t-cond { flex: 1.4; display: flex; align-items: center; gap: 6px; }
.t-age input { min-width: 0; }
.t-fee { flex: 1; }
.t-sum { flex: 0 0 84px; text-align: right; font-weight: 600; }
.t-del { flex: 0 0 24px; text-align: center; font-size: 18px; }
</style>
